import { List, Map } from "immutable";
import { createSelector } from "reselect";
import { recordChanged } from "common/helpers/Record";
import { ProductToOptionGroupLinkRecord } from "common/records/ProductToOptionGroupLink";
import { RootState } from "common/reducers";

export const model = "productToOptionGroupLinks";

/**
 * Loads the byId map
 *
 * @param {RootState} state The RootState
 * @returns {Map< number, ProductToOptionGroupLinkRecord>} The byId map
 */
export const byId = (state: RootState) => state.getIn([model, "byId"]);

/**
 * Loads the lastSavedById map
 *
 * @param {RootState} state The RootState
 * @returns {Map< number, ProductToOptionGroupLinkRecord>} The lastSavedById map
 */
export const lastSavedById = (state: RootState) => state.getIn([model, "lastSavedById"]);

export const productToOptionGroupLink = (state: RootState, id: number) => byId(state).get(id);

/**
 * Retrieves the lastSavedProductToOptionGroupLink record.
 * @param {RootState} state - The root state of the application.
 * @param {number} id - The ID of the ptog to retrieve the value for.
 * @returns {ProductToOptionGroupLinkRecord} ProductToOptionGroupLinkRecord
 */
export const productToOptionGroupLinkLastSaved = (state: RootState, id: number) => lastSavedById(state).get(id);

/**
 * Loads the ptog links id for provided option group id
 *
 * @param {RootState} state The RootState
 * @param {number} productId The ID of the product
 * @param {number} optionGroupId The ID of the option group
 * @returns {number | null}
 */
export const ptogFromOptionGroupId = (state: RootState, productId: number, optionGroupId: number): any => {
  const matchingLink = byId(state)
    .filter((ptog) => ptog.product_id === productId)
    .find((ptog) => ptog.option_group_id === optionGroupId);
  if (matchingLink) {
    return matchingLink.id;
  }
  return null;
};

/**
 * Builds a map indexing every ptog link by its product_id.
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, List<ProductToOptionGroupLinkRecord>>} The links grouped by product_id
 */
const ptogIndexByProductId = createSelector([byId], (byIdMap) =>
  byIdMap.reduce(
    (acc, ptog) => acc.update(ptog.product_id, List<ProductToOptionGroupLinkRecord>(), (list) => list.push(ptog)),
    Map<number, List<ProductToOptionGroupLinkRecord>>(),
  ),
);

/**
 * Loads the ptog links for the given product id.
 *
 * @param {RootState} state The RootState
 * @param {number} productId The ID of the product
 * @returns {List<ProductToOptionGroupLinkRecord>} The product's links, or an empty List if it has none
 */
export const ptogsByProductId = (state: RootState, productId: number): List<ProductToOptionGroupLinkRecord> =>
  ptogIndexByProductId(state).get(productId) ?? List();

/**
 * Checks if the ptog have changed on the given ptog id
 *
 * @param {RootState} state The RootState
 * @param {number} ptogId
 * @returns {boolean} True if the ptog has changed, false otherwise
 */
export const productToOptionGroupLinkChanged = (state: RootState, ptogId: number): boolean => {
  const productToOptionGroupLinkRecord = productToOptionGroupLink(state, ptogId);
  const productToOptionGroupLinkLastSavedRecord = productToOptionGroupLinkLastSaved(state, ptogId);
  if (productToOptionGroupLinkRecord && productToOptionGroupLinkLastSavedRecord) {
    return recordChanged(productToOptionGroupLinkRecord, productToOptionGroupLinkLastSavedRecord);
  }
  return false;
};
